import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { InspectionModelService, LanguageService } from '../../shared/services/index';
import { ApplicationSubType, Inspection } from '../../shared/models/index';
import { InspectionDetailMenu } from '../../menus/inspection.menu';
import { InspectionModelMenuComponent } from '../menus/inspection-model.menu';
import { BasePageComponent } from '../../base-page.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { OrderSummariesComponent } from '../../utils/component/order-summaries.component';

import * as _ from 'lodash';
import { zip } from 'rxjs';
import { ModalComponent } from '../../modal/modal.component';
import { InspectionModelTemplatesModalComponent } from '../components/templates-modal.component';

@Component({
    templateUrl: './children-list.component.html',
    standalone: false
})
export class InspectionModelChildrenListPageComponent extends BasePageComponent implements OnInit {
  models: any[];
  inspection: Inspection;
  errorMessage: string;
  inspectionId: string;
  @ViewChild('fileInput', { static: true })
  fileInput: ElementRef;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('sendToValidateConfirm', { static: true })
  sendToValidateConfirm: ConfirmComponent;
  @ViewChild('orderPlugins', { static: true }) orderPluginsModal: OrderSummariesComponent;
  @ViewChild('templatesModal', { static: true }) templatesModal: ModalComponent;
  plugins: any[];

  constructor(
    private inspectionModelService: InspectionModelService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private language: LanguageService,
    location: Location
  ) {
    super(location);
  }

  ngOnInit(): void {
    this.initRoute();
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;
      this.initData();
    });
  }

  initData(): void {
    zip(this.inspectionModelService.getModel(this.inspectionId), this.inspectionModelService.getModels()).subscribe(
      ([model, models]) => {
        this.inspection = model;
        this.models = models.filter((m) => m.applicationSubType !== ApplicationSubType.ParentInstallation);
        this.updateSelections();
      }
    );
  }

  get attachedModels() {
    return this.models?.filter((m) => m.selected);
  }

  openModel(inspection: Inspection): void {
    this.parentTemplateId = this.inspectionId;
    this.router.navigate([this.templatesRoute, inspection.id]);
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(InspectionModelMenuComponent, {
      inspection: this.inspection,
      currentPerson: this.currentPerson,
      hideModules: true
    });
  }

  public selectMenu(item: any) {
    switch (item) {
      case InspectionDetailMenu.Information:
        this.gotoInformationPage();
        break;
      case InspectionDetailMenu.Properties:
        this.gotoEditPage();
        break;
      case InspectionDetailMenu.ImportModule:
        break;
      case InspectionDetailMenu.AddPlugin:
        this.gotoAddPluginPage();
        break;
      case InspectionDetailMenu.CreateModule:
        break;
      case InspectionDetailMenu.ImportParts:
        this.openFileSelector();
        break;
      case InspectionDetailMenu.Delete:
        this.openDeleteConfirm();
        break;
      case InspectionDetailMenu.OrderModules:
        break;
      case InspectionDetailMenu.AssociateMachines:
        this.gotoAssociatesMachinePage();
        break;
      case InspectionDetailMenu.Status:
        this.gotoUpdateStatusPage();
        break;
      case InspectionDetailMenu.OrderPlugins:
        this.openOrderPluginsPage();
        break;
      case InspectionDetailMenu.DownloadTranslation:
        this.downloadTranslation();
        break;
      case InspectionDetailMenu.DeleteSelectedModules:
        break;
      default:
    }
  }

  public gotoUpdateStatusPage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'status']);
  }

  public gotoInformationPage() {
    return this.router.navigate([
      this.templatesRoute,
      this.inspection.id,
      'view',
      `${this.templatesRoute}/${this.inspection.id}/templates`
    ]);
  }

  public gotoAssociatesMachinePage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'categories', 'edit']);
  }

  public gotoEditPage() {
    return this.router.navigate([this.templatesRoute, this.inspection.id, 'edit']);
  }

  public gotoAddPluginPage() {
    return this.router.navigate([
      this.templatesRoute,
      this.inspection.id,
      'addPlugin',
      this.inspection.applicationSubType || ''
    ]);
  }

  public openFileSelector() {
    this.fileInput.nativeElement.click();
  }

  public upload(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files && files.length > 0) {
      const inputFile = new FormData();
      for (let i = 0; i < files.length; i++) {
        inputFile.append('files[' + i + ']', files[i], files[i].name);
      }

      this.inspectionModelService.uploadParts(this.inspectionId, inputFile).subscribe((data) => {
        // this.goToBack();
      });
    }
  }

  downloadTranslation() {
    this.language.downloadTranslationFiles([`inspection_${this.inspectionId}`]);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deleteInspection(e: any) {
    this.inspectionModelService.deleteInspection(this.inspectionId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  public openOrderPluginsPage() {
    const orderableItems = _.map(this.inspection.plugins, (p: any) => ({
      id: p.id,
      name: this.language.getInstantValue(`plugins.${p.pluginType}.name`) || p.name || p.pluginType,
      type: 'plugin',
      order: p.order
    }));

    this.orderPluginsModal.open(orderableItems);
  }

  public getPluginNameByPluginType(pluginType: string): string {
    const data = _.find(this.plugins, function (p: any) {
      return p.PluginType === pluginType;
    });
    if (data) {
      return data.Name;
    } else {
      return pluginType;
    }
  }

  savePluginsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order
    }));

    this.inspectionModelService.orderPlugins(this.inspectionId, orderableItems).subscribe((el) => {});
  }

  openTemplateAttachments() {
    this.templatesModal.hasClassModal = true;
    this.templatesModal.open(InspectionModelTemplatesModalComponent, {
      models: this.models
    });
  }

  saveAttachments(selectedModels: any[]) {
    if (selectedModels) {
      this.inspection.attachedTemplates = selectedModels;
      this.inspectionModelService.attachTemplates(this.inspectionId, this.inspection.attachedTemplates).subscribe();
    }
    this.updateSelections();
  }

  goToBack(): void {
    this.router.navigate([this.templatesRoute, 'parents']);
  }

  refreshPage() {
    this.refresh();
  }

  private updateSelections() {
    this.models.forEach((m) => (m.selected = !!this.inspection.attachedTemplates?.some((id) => id === m.id)));
  }
}
